import React, { useContext, ComponentType, FC } from 'react';
import { ThemeContext } from 'styled-components';
import { setConfiguration, ScreenClassProvider } from 'react-grid-system';
import hoistNonReactStatics from 'hoist-non-react-statics';

import throwError from '../../../utils/error/_throwError';

export interface ConfigGridProps {}

export interface ConfigGridOptions {
  useOwnWidth?: boolean;
}

type ConfigGridType = (
  (<T extends {}>(WrappedComponent: ComponentType<T>) => ComponentType<T & ConfigGridProps>) &
  ((options: ConfigGridOptions) => <T extends {}>(WrappedComponent: ComponentType<T>) => ComponentType<T & ConfigGridProps>)
);

export interface ConfigGridMock {}

/**
 * @since 0.14.0
 * @see See [App configuration](/Devguide/App%20configuration)
 * @param test {string} foobar
 */
const configGrid = <T extends {} = {}>(WrappedComponentOrConfig: ConfigGridOptions | ComponentType<T>) => {
  if (typeof WrappedComponentOrConfig === 'function' || (WrappedComponentOrConfig as any).type || (WrappedComponentOrConfig as any).render) {
    return withGridConfig(WrappedComponentOrConfig as FC<T>);
  }
  return (WrappedComponent: FC<T>) => withGridConfig(WrappedComponent, WrappedComponentOrConfig);

  function withGridConfig(WrappedComponent: FC<T>, { useOwnWidth = true } = {}) {
    let init = false;
    const ConfigGrid = (props: T) => {
      const theme = useContext(ThemeContext);
      const { breakpoints } = theme;

      if (!breakpoints) {
        throwError('configGrid: Your theme is missing the "breakpoints" key. Make sur "configTheme" is defined before "configGrid" and extends "baseTheme"');
      } else if (!init) {
        init = true;
        setConfiguration({
          gutterWidth: 32,
          breakpoints: [
            breakpoints.SM[0],
            breakpoints.MD[0],
            breakpoints.LG[0],
            breakpoints.LL[0],
            breakpoints.XL[0],
            breakpoints.XXL[0],
          ],
        });
      }

      return (
        <ScreenClassProvider useOwnWidth={useOwnWidth}>
          <WrappedComponent {...props} />
        </ScreenClassProvider>
      );
    };

    return hoistNonReactStatics(ConfigGrid, WrappedComponent);
  }
};

/** @component */
export default configGrid as ConfigGridType;
